import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedSpinner } from '../../../components/AnimatedSpinner';
import { CopyIcon } from '../../../components/Icons';
import { ToolbarButton } from '../../../components/ToolbarButton/ToolbarButton';
import { isRTL, processTextDirection } from '../../../utils/textDirection';

// Simple markdown to HTML converter for basic formatting
const convertMarkdownToHtml = (markdown: string): string => {
  let html = markdown;

  // Convert line breaks
  html = html.replace(/\n/g, '<br>');

  // Convert bold **text** or __text__
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

  // Convert italic *text* or _text_
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');

  // Convert code blocks ```code```
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  // Convert inline code `code`
  html = html.replace(/`(.*?)`/g, '<code>$1</code>');

  // Convert headers
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');

  // Convert unordered lists
  html = html.replace(/^\* (.*$)/gm, '<li>$1</li>');
  html = html.replace(/^- (.*$)/gm, '<li>$1</li>');

  // Convert ordered lists
  html = html.replace(/^\d+\. (.*$)/gm, '<li>$1</li>');

  // Wrap consecutive <li> elements in <ul> or <ol>
  html = html.replace(/(<li>.*<\/li>)/gs, (match) => {
    // Check if it's an ordered list (starts with number)
    if (markdown.match(/^\d+\./m)) {
      return `<ol>${match}</ol>`;
    } else {
      return `<ul>${match}</ul>`;
    }
  });

  // Convert paragraphs (double line breaks)
  html = html.replace(/\n\n/g, '</p><p>');
  html = `<p>${html}</p>`;

  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p><br><\/p>/g, '<br>');

  return html;
};

interface ArtifactPanelProps {
  showPanel: boolean;
  text: string;
  isLoading: boolean;
  handleClosePanel: () => void;
  artifactName?: string;
}

export const ArtifactPanel: React.FC<ArtifactPanelProps> = ({
  showPanel,
  text,
  isLoading,
  handleClosePanel,
  artifactName,
}) => {
  const { t } = useTranslation();
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [size, setSize] = useState({ width: 500, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  const [isContentInitialized, setIsContentInitialized] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = window.innerWidth < 768;

  // Save the selection (caret or range) as character offsets from the start
  // of the element. Returns null if there's no selection inside `element`.
  const saveSelectionOffsets = (
    element: HTMLElement,
  ): { start: number; end: number } | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    if (!element.contains(range.startContainer)) return null;

    const startRange = range.cloneRange();
    startRange.selectNodeContents(element);
    startRange.setEnd(range.startContainer, range.startOffset);
    const start = startRange.toString().length;

    const endRange = range.cloneRange();
    endRange.selectNodeContents(element);
    endRange.setEnd(range.endContainer, range.endOffset);
    const end = endRange.toString().length;

    return { start, end };
  };

  // Restore selection by walking text nodes and placing both endpoints at the
  // requested character offsets.
  const restoreSelectionOffsets = (
    element: HTMLElement,
    offsets: { start: number; end: number },
  ) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let startNode: Node | null = null;
    let startNodeOffset = 0;
    let endNode: Node | null = null;
    let endNodeOffset = 0;
    let consumed = 0;
    let node: Node | null = walker.nextNode();

    while (node) {
      const len = node.textContent?.length ?? 0;
      if (!startNode && consumed + len >= offsets.start) {
        startNode = node;
        startNodeOffset = offsets.start - consumed;
      }
      if (consumed + len >= offsets.end) {
        endNode = node;
        endNodeOffset = offsets.end - consumed;
        break;
      }
      consumed += len;
      node = walker.nextNode();
    }

    if (!startNode || !endNode) return;
    const range = document.createRange();
    range.setStart(startNode, startNodeOffset);
    range.setEnd(endNode, endNodeOffset);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const executeCommandWithCursor = (command: string, value?: string) => {
    if (!contentRef.current) return;

    // Focus the content area first
    contentRef.current.focus();

    // Execute the command
    document.execCommand(command, false, value);

    // Update our state with the new content
    setEditableContent(contentRef.current.innerHTML);
  };

  // Dragging functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || isMobile) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  // Resize functionality
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    e.stopPropagation(); // Prevent dragging when resizing
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };

  const handleResizeMouseMove = (e: MouseEvent) => {
    if (!isResizing || isMobile) return;
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    const newWidth = Math.max(300, resizeStart.width + deltaX); // Min width 300px
    const newHeight = Math.max(200, resizeStart.height + deltaY); // Min height 200px

    setSize({
      width: newWidth,
      height: newHeight,
    });
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, resizeStart]);

  // Control render state for animation
  useEffect(() => {
    if (showPanel) {
      setShouldRender(true);
      // Start animation after component mounts
      const timer = setTimeout(() => setIsAnimatingIn(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimatingIn(false);
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => setShouldRender(false), 500);
      return () => clearTimeout(timer);
    }
  }, [showPanel]);

  // Set initial position on right side
  useEffect(() => {
    if (showPanel && !isMobile) {
      setPosition({
        x: window.innerWidth - 600, // Position on right side
        y: 100,
      });
    }
  }, [showPanel]);

  // Initialize editable content when text changes
  useEffect(() => {
    if (text) {
      const processedContent = processTextDirection(text);
      const htmlContent = convertMarkdownToHtml(processedContent);
      setEditableContent(htmlContent);
      setIsContentInitialized(true);

      // Preserve the caret or active selection across the innerHTML rewrite so
      // streaming chunks don't reset the cursor or cancel an in-progress drag-select.
      if (contentRef.current) {
        const offsets = saveSelectionOffsets(contentRef.current);
        contentRef.current.innerHTML = htmlContent;
        if (offsets) {
          restoreSelectionOffsets(contentRef.current, offsets);
        }
      }
    }
  }, [text]);

  // Update content ref when editable content changes (only when not actively editing)
  useEffect(() => {
    if (
      contentRef.current &&
      editableContent &&
      isContentInitialized &&
      !isUserEditing
    ) {
      contentRef.current.innerHTML = editableContent;
    }
  }, [editableContent, isContentInitialized, isUserEditing]);

  const handleCopyClick = () => {
    const node = contentRef.current;
    const plain = node?.innerText || text || '';
    const html = node?.innerHTML || '';
    if (!plain) return;

    const showOk = () => {
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 2000);
    };

    // Write both text/html and text/plain so rich editors keep formatting
    // (bold, italic, lists) while plain-text targets still get the text.
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      navigator.clipboard
        .write([
          new ClipboardItem({
            'text/plain': new Blob([plain], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ])
        .then(showOk)
        .catch(() => {
          navigator.clipboard
            .writeText(plain)
            .then(showOk)
            .catch((err) => console.error('Failed to copy:', err));
        });
    } else {
      navigator.clipboard
        .writeText(plain)
        .then(showOk)
        .catch((err) => console.error('Failed to copy:', err));
    }
  };

  const contentDirection = text ? isRTL(text) : false;

  if (!shouldRender) return null;

  return (
    <div
      ref={windowRef}
      className={`fixed z-[1001] bg-gray-500/20 backdrop-blur-xl border border-gray-400/30 rounded-xl shadow-2xl select-none transition-transform duration-500 ease-out pb-6 ${
        isAnimatingIn && showPanel
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-full'
      } ${isMobile ? 'left-4 right-4 bottom-4 top-[40%]' : ''}`}
      style={
        !isMobile
          ? {
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: `${size.width}px`,
              height: `${size.height}px`,
            }
          : {}
      }>
      {/* Mac-style Header */}
      <div
        className="flex items-center justify-between p-4 bg-gray-500/10 backdrop-blur-sm border-b border-gray-400/15 rounded-t-xl cursor-move"
        onMouseDown={handleMouseDown}>
        {/* Title */}
        <h3 className="text-white font-medium text-sm truncate">
          {artifactName || t('generation.artifact')}
        </h3>

        {/* Header Buttons */}
        <div className="flex items-center gap-2">
          {/* Copy Button */}
          <button
            className="p-1.5 hover:bg-gray-400/30 rounded transition-colors duration-150 text-gray-300 hover:text-white relative"
            onClick={handleCopyClick}
            title={t('generation.copyToClipboard')}>
            <CopyIcon />
            {showCopyTooltip && (
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {t('generation.copiedToClipboard')}
              </div>
            )}
          </button>

          {/* Close Button */}
          <button
            className="p-1.5 hover:bg-gray-400/30 rounded transition-colors duration-150 text-gray-300 hover:text-white"
            onClick={handleClosePanel}
            title={t('generation.close')}>
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>
      </div>

      {/* Rich Text Editor Toolbar - Fixed at Top */}
      <div className="absolute top-17 left-10 flex items-left gap-1 z-20">
        {/* Formatting buttons */}
        <ToolbarButton
          title={t('toolbar.bold')}
          onClick={() => executeCommandWithCursor('bold')}
          icon="fa-solid fa-bold"
        />
        <ToolbarButton
          title={t('toolbar.italic')}
          onClick={() => executeCommandWithCursor('italic')}
          icon="fa-solid fa-italic"
        />
        <ToolbarButton
          title={t('toolbar.underline')}
          onClick={() => executeCommandWithCursor('underline')}
          icon="fa-solid fa-underline"
        />

        {/* Alignment buttons */}
        <ToolbarButton
          title={t('toolbar.alignLeft')}
          onClick={() => executeCommandWithCursor('justifyLeft')}
          icon="fa-solid fa-align-left"
        />
        <ToolbarButton
          title={t('toolbar.alignCenter')}
          onClick={() => executeCommandWithCursor('justifyCenter')}
          icon="fa-solid fa-align-center"
        />
        <ToolbarButton
          title={t('toolbar.alignRight')}
          onClick={() => executeCommandWithCursor('justifyRight')}
          icon="fa-solid fa-align-right"
        />

        {/* List buttons */}
        <ToolbarButton
          title={t('toolbar.numberedList')}
          onClick={() => executeCommandWithCursor('insertOrderedList')}
          icon="fa-solid fa-list-ol"
        />
        <ToolbarButton
          title={t('toolbar.bulletPoints')}
          onClick={() => executeCommandWithCursor('insertUnorderedList')}
          icon="fa-solid fa-list-ul"
        />
      </div>

      {/* Content Area */}
      <div
        className={`overflow-y-auto text-gray-100 h-[calc(100%-100px)] bg-transparent mt-8 ${
          contentDirection ? 'rtl-content' : ''
        }`}>
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-500/20 backdrop-blur-xl z-10 mt-[70px]">
            <AnimatedSpinner />
          </div>
        )}

        {/* Original Content */}
        <div className="tc-artifact-container p-4 pb-6">
          <div className="tc-artifact-desc">
            <div className="tc-artifact-desc-details">
              {/* Editable Content Area */}
              <div
                ref={contentRef}
                contentEditable={true}
                className="mixed-content min-h-[200px] outline-none"
                onFocus={() => {
                  setIsUserEditing(true);
                }}
                onInput={() => {
                  // Don't update state during typing to prevent cursor jumping
                  // The content is already in the DOM, no need to sync back to state
                }}
                onBlur={(e) => {
                  // Save content when user finishes editing
                  const newContent = e.currentTarget.innerHTML;
                  setEditableContent(newContent);
                  setIsUserEditing(false);
                }}
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
                suppressContentEditableWarning={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Resize Handle - Bottom Right Corner */}
      {!isMobile && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gray-400/30 hover:bg-gray-400/50 transition-colors"
          onMouseDown={handleResizeMouseDown}
          style={{
            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
          }}
        />
      )}
    </div>
  );
};
